<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  isChecked?: boolean
  checkedIcon?: string
  uncheckedIcon?: string
}
const props = withDefaults(defineProps<Props>(), {
  isChecked: false,
})

interface Emits {
  (e: 'update:isChecked', value: boolean): void
}
const emit = defineEmits<Emits>()

const checkedIconClass = computed(() => {
  return props.checkedIcon ? props.checkedIcon : 'mi-check'
})

const uncheckedIconClass = computed(() => {
  return props.uncheckedIcon ? props.uncheckedIcon : 'mi-close'
})
</script>

<template>
  <div
    class="toggle"
    :class="{ 'is-checked': props.isChecked }"
    @click="emit('update:isChecked', !props.isChecked)"
  >
    <div class="dot">
      <i class="icon checked mi-outlined" :class="checkedIconClass" />
      <i class="icon unchecked mi-outlined" :class="uncheckedIconClass" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;

.toggle {
  width: 52px;
  height: 32px;
  border-radius: 100px;
  position: relative;
  cursor: pointer;
  transition: all 0.1s cubic-bezier(0.55, 0, 0.1, 1);
  background: #e6e6e61f;
  border: 2px solid #17191a1f;
  box-sizing: border-box;

  &:hover {
    border-color: #005291;
  }

  &.is-checked {
    background: map.get(themeVariables.$colors, primary);
    border-color: transparent;
  }
}

.dot {
  position: relative;
  //background: hsl(0 0 66%);
  height: calc(100% - 4px);
  aspect-ratio: 1/1;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: all 0.1s cubic-bezier(0.55, 0, 0.1, 1);
  border: 2px solid #a5a5a6;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #a5a5a6;

  .toggle:hover & {
    background: map.get(themeVariables.$colors, primary);
    border-color: transparent;
    color: white;
  }
  .toggle.is-checked & {
    background: hsl(208deg, 57%, 95%);
    left: calc(50% - 2px);
    border-color: transparent;
    color: #001e3d;

    .erebus-app[data-theme='dark'] & {
      background: #032b54;
      color: map.get(themeVariables.$colors, primary);
    }
  }
}

.icon {
  position: absolute;
  transition: opacity 0.1s cubic-bezier(0.55, 0, 0.1, 1),
    transform 0.1s cubic-bezier(0.55, 0, 0.1, 1), visibility 0.1s cubic-bezier(0.55, 0, 0.1, 1);

  &.checked {
    visibility: hidden;
    opacity: 0;
    transform: scale(0.85);
  }

  .toggle.is-checked &.checked {
    visibility: visible;
    opacity: 1;
    transform: scale(1);
  }

  .toggle.is-checked &.unchecked {
    visibility: hidden;
    opacity: 0;
    transform: scale(0.85);
  }
}
</style>
